"use client";
import Image from "next/image";
import { type CSSProperties, type SyntheticEvent, useState } from "react";
import styles from "./styles.module.css";

interface ImageWithLoaderProps {
	src: string;
	alt: string;
	width?: number;
	height?: number;
	fill?: boolean;
	objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
	className?: string;
	imageClassName?: string;
	fallbackSrc?: string;
	borderRadius?: number;
	skeletonType?: "shimmer" | "pulse";
	skeletonColor?: "default" | "blue" | "gray" | "warm";
	transitionDuration?: number;
	priority?: boolean;
	quality?: number;
	sizes?: string;
	placeholder?: "blur" | "empty";
	blurDataURL?: string;
	transparent?: boolean;
}

const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({
	src,
	alt,
	width,
	height,
	fill = false,
	objectFit = "contain",
	className = "",
	imageClassName = "",
	fallbackSrc = "/imgs/no-photo.png",
	borderRadius = 8,
	skeletonType = "shimmer",
	skeletonColor = "default",
	transitionDuration = 0.3,
	priority = false,
	quality = 75,
	sizes,
	placeholder = "empty",
	blurDataURL,
	transparent,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);

	const containerStyle: CSSProperties = fill
		? {
				position: "relative",
				borderRadius: `${borderRadius}px`,
				overflow: "hidden",
				backgroundColor: transparent ? "transparent" : "#f8f9fa",
			}
		: {
				width: `${width}px`,
				height: `${height}px`,
				borderRadius: `${borderRadius}px`,
				position: "relative",
				overflow: "hidden",
				backgroundColor: transparent ? "transparent" : "#f8f9fa",
			};

	const imageStyle: CSSProperties = {
		objectFit,
		opacity: isLoading ? 0 : 1,
		transition: `opacity ${transitionDuration}s ease`,
	};

	const skeletonClassName =
		skeletonType === "pulse"
			? `${styles.skeleton} ${styles.skeletonPulse}`
			: styles.skeleton;

	const handleLoad = () => {
		setIsLoading(false);
	};

	const handleError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
		setIsLoading(false);
		setHasError(true);
		const target = e.target as HTMLImageElement;
		target.src = fallbackSrc;
		target.srcset = fallbackSrc;
	};

	return (
		<div
			className={`${styles.imageContainer} ${className}`}
			style={containerStyle}
			data-skeleton-color={
				skeletonColor !== "default" ? skeletonColor : undefined
			}
		>
			{isLoading && !hasError && <div className={skeletonClassName} />}

			<Image
				src={src}
				alt={alt}
				{...(fill ? { fill: true } : { width: width!, height: height! })}
				style={imageStyle}
				className={`${styles.image} ${imageClassName}`}
				onLoad={handleLoad}
				onError={handleError}
				priority={priority}
				quality={quality}
				sizes={sizes}
				placeholder={placeholder}
				blurDataURL={blurDataURL}
			/>
		</div>
	);
};

export default ImageWithLoader;
